<template>
  <div class="media-container">
    <!-- Empty State -->
    <div v-if="!loading && gallery.length === 0" class="empty-media-state pa-12">
      <v-icon size="80" color="var(--border)">mdi-video-off-outline</v-icon>
      <h3 class="mt-4 font-weight-bold">No Videos Found</h3>
      <p class="text-tx3">Begin by uploading MP4 videos to this album using the 'Add Media' button.</p>
    </div>

    <!-- Video Grid -->
    <div v-else class="media-grid">
      <div
        v-for="(item, index) in gallery"
        :key="item.id"
        class="media-card-wrap"
      >
        <v-hover v-slot="{ hover }">
          <div class="media-card shadow-sm" :class="{ 'elevated': hover }">
            <div class="video-thumb-container cursor-pointer" @click="openViewer(index)">
              <!-- Native video for thumb -->
              <video
                class="video-element-thumb"
                :src="API_BASE_URL + '/uploads/' + item.document_url"
                preload="metadata"
                muted
                playsinline
                @loadedmetadata="seekToFirstFrame"
              ></video>
              
              <!-- Play Icon Overlay -->
              <div class="video-play-overlay">
                <div class="play-icon-circle">
                  <v-icon color="white" size="32">mdi-play</v-icon>
                </div>
              </div>
            </div>

            <!-- Actions Overlay -->
            <div class="media-actions-overlay" :class="{ 'visible': hover }">
              <v-btn
                small
                icon
                class="media-action-btn delete-btn"
                @click.stop="confirmDelete(item.id)"
              >
                <v-icon size="18">mdi-trash-can-outline</v-icon>
              </v-btn>
              <v-btn
                small
                icon
                class="media-action-btn zoom-btn"
                @click.stop="openViewer(index)"
              >
                <v-icon size="18">mdi-play-circle-outline</v-icon>
              </v-btn>
            </div>
          </div>
        </v-hover>
      </div>
    </div>

    <!-- Media Viewer -->
    <MediaViewer
      v-model="imageDialog"
      :items="mediaItems"
      :index="selected_item"
    />

    <!-- Delete Confirmation -->
    <ConfirmModal
      v-model="deleteDialog"
      type="danger"
      title="Delete Video"
      message="Are you sure you want to delete this video permanently?"
      confirm-text="Yes, Delete"
      cancel-text="Cancel"
      :loading="deleteLoading"
      @confirm="deleteDocument"
    />
  </div>
</template>

<script>
import { mapMutations } from "vuex";
import ConfirmModal from "../../common/ConfirmModal.vue";
import MediaViewer from "../../common/MediaViewer.vue";

export default {
  name: 'VideoGallery',
  components: { ConfirmModal, MediaViewer },
  props: ["loading"],
  data() {
    return {
      selected_item: 0,
      gallery: [],
      actual_gallery: [],
      imageDialog: false,
      delete_id: "",
      deleteDialog: false,
      deleteLoading: false,
    };
  },
  computed: {
    API_BASE_URL() { return process.env.VUE_APP_API_URL; },
    mediaItems() {
      return this.actual_gallery.map(doc => ({
        url: this.API_BASE_URL + '/uploads/' + doc.document_url,
        type: 'video',
        name: doc.document_url
      }));
    }
  },
  watch: {
    loading: function () { if (!this.loading) this.getDetails(); }
  },
  methods: {
    ...mapMutations("alert", ["setAlert"]),
    getDetails() {
      this.$axios.get(`societyadmin/gallery/` + this.$route.params.id + `/video`)
        .then((response) => {
          if (response.data.status) {
            this.gallery = response.data.gallery;
            this.actual_gallery = response.data.gallery;
          } else this.showErr(response.data.message);
        });
    },
    deleteDocument() {
      this.deleteLoading = true;
      this.$axios.delete("societyadmin/gallery/documents/" + this.delete_id)
        .then((response) => {
          this.deleteLoading = false;
          if (response.data.status) {
            this.getDetails();
            this.deleteDialog = false;
            this.setAlert({ showAlert: true, variant: "green accent-4", timeout: "2000", message: "Video deleted" });
          } else this.showErr(response.data.message);
        }).catch(() => { this.deleteLoading = false; });
    },
    openViewer(index) {
      this.selected_item = index;
      this.imageDialog = true;
    },
    closeViewer() { this.imageDialog = false; },
    prevItem() {
      this.selected_item = (this.selected_item - 1 + this.actual_gallery.length) % this.actual_gallery.length;
    },
    nextItem() {
      this.selected_item = (this.selected_item + 1) % this.actual_gallery.length;
    },
    confirmDelete(id) {
      this.delete_id = id;
      this.deleteDialog = true;
    },
    seekToFirstFrame(event) { event.target.currentTime = 0.1; },
    showErr(msg) { this.setAlert({ showAlert: true, variant: "red accent-3", timeout: "2000", message: msg }); }
  },
  created() {
    this.getDetails();
  },
};
</script>

<style scoped>
.media-container { min-height: 200px; }

.empty-media-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  border: 2px dashed var(--border); border-radius: 20px; color: var(--tx3); text-align: center;
}

/* ── Media Grid ── */
.media-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 20px;
}

.media-card {
  position: relative; border-radius: 16px; overflow: hidden; background: #111;
  border: 1px solid var(--border); transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.media-card.elevated { transform: translateY(-4px); border-color: var(--brand); box-shadow: 0 12px 30px rgba(0,0,0,0.1); }

.video-thumb-container { position: relative; aspect-ratio: 1; overflow: hidden; }
.video-element-thumb { width: 100%; height: 100%; object-fit: cover; }

.video-play-overlay {
  position: absolute; inset: 0; background: rgba(0,0,0,0.3);
  display: flex; align-items: center; justify-content: center;
}
.play-icon-circle {
  width: 54px; height: 54px; border-radius: 50%;
  background: rgba(255, 255, 255, 0.15); border: 2px solid rgba(255, 255, 255, 0.4);
  display: flex; align-items: center; justify-content: center;
  backdrop-filter: blur(4px); transition: transform 0.2s;
}
.media-card:hover .play-icon-circle { transform: scale(1.1); }

.media-actions-overlay {
  position: absolute; inset: 0; background: rgba(15, 23, 42, 0.1);
  display: flex; align-items: flex-start; justify-content: flex-end; padding: 8px;
  opacity: 0; transition: opacity 0.3s;
}
.media-actions-overlay.visible { opacity: 1; }

.media-action-btn { background: rgba(255, 255, 255, 0.9) !important; border-radius: 8px !important; color: var(--tx1) !important; box-shadow: 0 4px 12px rgba(0,0,0,0.1); margin-left: 6px; }
.delete-btn:hover { color: #EF4444 !important; }

/* ── Premium Lightbox ── */
.glb-wrap {
  background: var(--surface);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.glb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border);
  background: var(--surface2);
  flex-shrink: 0;
}

.glb-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 700;
  color: var(--tx);
}

.glb-counter {
  font-size: 12px;
  font-weight: 600;
  color: var(--tx3);
  background: var(--surface);
  border: 1px solid var(--border);
  padding: 2px 10px;
  border-radius: 20px;
}

.glb-actions {
  display: flex;
  align-items: center;
  gap: 6px;
}

.glb-btn {
  width: 32px; height: 32px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background: var(--surface);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: all 0.15s;
}
.glb-btn:hover { background: var(--surface3); border-color: var(--brand); }
.glb-btn:disabled { opacity: 0.35; cursor: not-allowed; }
.glb-btn.glb-close-btn:hover { background: rgba(239,68,68,0.08); border-color: #EF4444; }
.glb-btn.glb-close-btn:hover .v-icon { color: #EF4444 !important; }

/* Body */
.glb-body {
  position: relative;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  max-height: 60vh;
}

.glb-video {
  max-width: 100%;
  max-height: 60vh;
  outline: none;
  display: block;
}

/* Side Arrows */
.glb-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 40px; height: 40px;
  border-radius: 50%;
  background: rgba(255,255,255,0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; transition: background 0.2s;
}
.glb-nav:hover { background: rgba(255,255,255,0.25); }
.glb-prev { left: 12px; }
.glb-next { right: 12px; }

/* Thumbnails strip */
.glb-thumbnails {
  display: flex;
  gap: 8px;
  padding: 12px 16px;
  overflow-x: auto;
  background: var(--surface2);
  border-top: 1px solid var(--border);
}
.glb-thumbnails::-webkit-scrollbar { height: 4px; }
.glb-thumbnails::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

.glb-thumb {
  flex-shrink: 0;
  width: 56px; height: 56px;
  border-radius: 8px;
  overflow: hidden;
  border: 2px solid transparent;
  cursor: pointer; opacity: 0.6; transition: all 0.2s;
  background: #1a1a1a;
}
.glb-thumb-video-wrap {
  width: 100%; height: 100%;
  display: flex; align-items: center; justify-content: center;
}
.glb-thumb:hover { opacity: 0.85; }
.glb-thumb-active { border-color: var(--brand); opacity: 1; }

@media (max-width: 600px) {
  .media-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
}
</style>

<style>
.gallery-lightbox-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 64px rgba(0,0,0,0.25) !important;
}
</style>
